import {onlineCalculatorsApiActions, onlineCalculatorsApiSelectors} from '@esprit/online-calculators';
import {LOCATION_CHANGE, push} from 'connected-react-router';
import {delay} from 'q';
import {generatePath} from 'react-router';
import {getContext} from 'redux-saga-test-plan/matchers';
import {all, call, put, race, select, spawn, take, takeEvery} from 'redux-saga/effects';
import {t} from 'i18next';
import {Link} from 'react-router-dom';
import i18n from '../../../i18n';
import type {
    IApplicationLocation,
    ICustomerBase,
    IFetchVaultsRequest,
    INotification,
    INotificationClientAdvisory,
    IUserInfo,
    IUserMailbox,
    IVault,
} from '../../api/domain';
import type {SettingsApiService, SynchronizationApiService} from '../../api/services';
import {AuthenticationUrl, NotificationType, RouteTranslationKey, UserRole} from '../../enum';
import {Tenant, TenantCodes} from '../../enum/tenant';
import {addLanguageCodeToUrl} from '../../utils/i18n-helpers';
import {scrollWindowToTop} from '../../utils/window';
import {
    fetchSettings as fetchAppointmentFinderSettings,
    selectCustomerBaseForAppointment
} from '../appointments-api/appointments-api-actions';
import {
    fetchCustomerBases,
    fetchCustomerBasesFailure,
    fetchCustomerBasesSuccess,
    readDefaultCustomerBase,
    readDefaultCustomerBaseFailure,
    readDefaultCustomerBaseSuccess,
    resolveRmForCustomerBase,
    resolveRmForCustomerBaseFailure,
    resolveRmForCustomerBaseSuccess,
} from '../customer-base-api/customer-base-api-actions';
import {getCustomerBases, getDefaultCustomerBase} from '../customer-base-api/customer-base-api-selectors';
import {fetchUserPerson} from '../customer-master-data-api/customer-master-data-api-actions';
import {logError} from '../logging-api/logging-api-actions';
import {
    fetchUserPortalAccount,
    fetchUserPortalAccountFailure,
    fetchUserPortalAccountSuccess
} from '../portal-account-api/portal-account-api-actions';
import {
    fetchMediaTypes,
    fetchSettings as fetchSecureDocumentStoreSettings,
    fetchTags,
    fetchUnreadDocumentCount,
    fetchVaults,
    fetchVaultsFailure,
    fetchVaultsSuccess,
} from '../secure-document-store-api/secure-document-store-api-actions';
import {getOrderedVaults} from '../secure-document-store-api/secure-document-store-api-selectors';
import {
    fetchMailboxes,
    fetchMailboxesFailure,
    fetchMailboxesSuccess,
    fetchSettings as fetchSecureInboxSettings,
    fetchTopics,
} from '../secure-inbox-api/secure-inbox-api-actions';
import {getOrderedMailboxes} from '../secure-inbox-api/secure-inbox-api-selectors';
import {fetchLanguages, fetchSettings as fetchCustomizationSettings} from '../settings-api/settings-api-actions';
import {fetchUserInfo, fetchUserInfoFailure, fetchUserInfoSuccess} from '../user-info-api/user-info-api-actions';
import {getUserInfo} from '../user-info-api/user-info-api-selectors';
import type {Action} from '../utils';
import {
    addNotification,
    addNotificationClientAdvisory,
    addNotificationSuccess,
    applicationInitialized,
    changeLocation,
    changeLocationFailure,
    changeLocationSuccess,
    changeTenant,
    hideProgressIndicator,
    initializeApplication,
    login,
    logout,
    selectCustomerBase,
    selectCustomerBaseForEServices,
    selectCustomerBaseForProducts,
    selectMailboxes,
    selectVault,
    setConsentMatomo,
    setConsentMatomoFailure,
    setConsentMatomoSuccess,
    setRelationshipManager,
    strongLogin,
} from './application-actions';
import {isUserProspect} from './application-selectors';
import de from './translations/de.json';
import en from './translations/en.json';
import fr from './translations/fr.json';
import it from './translations/it.json';
import {fetchAllPopupResponses} from '../process-engine-api/process-engine-api-actions';

const loginStatusCheckIntervalInMs = 30 * 1000;

/* istanbul ignore next */
const tenantsMapping = {
    ...(process.env.REACT_APP_TENANT_BANK_XRAY && {[process.env.REACT_APP_TENANT_BANK_XRAY]: Tenant.WIR}),
    ...(process.env.REACT_APP_TENANT_BANK_YANKEE && {[process.env.REACT_APP_TENANT_BANK_YANKEE]: Tenant.BSU}),
    ...(process.env.REACT_APP_TENANT_BANK_ZULU && {[process.env.REACT_APP_TENANT_BANK_ZULU]: Tenant.SLF}),
    ...(process.env.REACT_APP_TENANT_BANK_WHISKEY && {[process.env.REACT_APP_TENANT_BANK_WHISKEY]: Tenant.SLM}),

    [TenantCodes.ABS]: Tenant.ABS,
    [TenantCodes.AKS]: Tenant.AKS,
    [TenantCodes.ARB]: Tenant.ARB,
    [TenantCodes.BLB]: Tenant.BLB,
    [TenantCodes.BSD]: Tenant.BSD,
    [TenantCodes.BSU]: Tenant.BSU,
    [TenantCodes.BKT]: Tenant.BKT,
    [TenantCodes.EEK]: Tenant.EEK,
    [TenantCodes.GRB]: Tenant.GRB,
    [TenantCodes.LPZ]: Tenant.LPZ,
    [TenantCodes.SBS]: Tenant.SBS,
    [TenantCodes.SKS]: Tenant.SKS,
    [TenantCodes.SLF]: Tenant.SLF,
    [TenantCodes.SLM]: Tenant.SLM,
    [TenantCodes.TI8M]: Tenant.TI8M,
    [TenantCodes.WIR]: Tenant.WIR,
};

// ### WARNING ###
// If you change this please change https://gitlab.ti8m.ch/esprit-fusion/matomo-analytics/-/blob/master/fpm-matomo/sources/matomo/plugins/EspritSites/EspritSites.php too
// ###############
const tenantSiteIdMapping = {
    [Tenant.ABS]: 1,
    [Tenant.AKS]: 2,
    [Tenant.ARB]: 3,
    [Tenant.BKT]: 5,
    [Tenant.BLB]: 6,
    [Tenant.BSD]: 7,
    [Tenant.BSU]: 8,
    [Tenant.EEK]: 10,
    [Tenant.GRB]: 13,
    [Tenant.LPZ]: 14,
    [Tenant.SBS]: 15,
    [Tenant.SKS]: 16,
    [Tenant.SLF]: 17,
    [Tenant.SLM]: 18,
    [Tenant.WIR]: 19,
};

/* istanbul ignore next */
const DEFAULT_TENANT = process.env.NODE_ENV === 'development' ? Tenant.TI8M : Tenant.NO_TENANT;

const getTenantByIdFromHeader = (headers: {
    'x-tnt': string
}): Tenant => (tenantsMapping[headers?.['x-tnt']] || DEFAULT_TENANT) as Tenant;

function ensureLanguageKeyIsInUrl() {
    const routeLanguageCode = /\/(de|fr|it|en)/i.exec(window.location.pathname);
    if (routeLanguageCode?.[1]) {
        return;
    }

    window.location.pathname = addLanguageCodeToUrl(i18n.language, window.location.pathname, process.env.PUBLIC_URL);
}

function loadTenantSpecificFavicon(tenant: Tenant) {
    const favicon = require(`../../static/tenants/${tenant}/icons/favicon.ico`).default;
    const link: HTMLLinkElement = document.querySelector("link[rel*='icon']") || document.createElement('link');
    link.type = 'image/x-icon';
    link.rel = 'shortcut icon';
    link.href = favicon;
    document.getElementsByTagName('head')[0].appendChild(link);
}

function loadTenantSpecificMatomoConfiguration(tenant: Tenant) {
    if (tenant in tenantSiteIdMapping) {
        window._paq = window._paq ?? [];
        const _paq = window._paq;
        _paq.push(['requireConsent']); // Once the function requireConsent is executed then no tracking request will be sent to Matomo and no cookies will be set
        _paq.push(['setDoNotTrack', true]); // Respect the user's privacy by not tracking, if the browser has do-not-track enabled, and it is enabled in Matomo prefs.
        _paq.push(['trackPageView']);
        _paq.push(['enableLinkTracking']);
        _paq.push(['setTrackerUrl', '/analytics/matomo.php']);
        _paq.push(['setSiteId', `${tenantSiteIdMapping[tenant]}`]);

        const script: HTMLScriptElement = document.createElement('script');
        script.type = 'text/javascript';
        script.async = true;
        script.defer = true;
        script.src = '/analytics/matomo.js';

        document.getElementsByTagName('head')[0].appendChild(script);
    }
}

function loadTenantResources(tenant: Tenant) {
    loadTenantSpecificMatomoConfiguration(tenant);
    loadTenantSpecificFavicon(tenant);
    /* istanbul ignore next */
    if (process.env.NODE_ENV === 'development') {
        // hot code replacement while developing
        require(`../../static/tenants/${tenant}/scss/style.scss`);
        require(`../../static/tenants/${tenant}/icons/IconComponentExport`);
    } else {
        // code splitting for production build
        import(`../../static/tenants/${tenant}/scss/style.scss`);
        import(`../../static/tenants/${tenant}/icons/IconComponentExport`);
    }
}

function* handleSetConsentMatomo({payload}: Action<{ allowTracking: boolean }>) {
    const {allowTracking} = payload;
    try {
        // _paq must be set at this point
        const _paq = window._paq;
        if (allowTracking) {
            _paq.push(['rememberConsentGiven', 2_160]); // 2nd param: remember in hours, 90 days (24*90)
        } else {
            _paq.push(['forgetConsentGiven']);
        }
        yield put(setConsentMatomoSuccess());
    } catch (e: unknown) {
        yield put(setConsentMatomoFailure());
        yield put(logError(`setConsentMatomo failed with: ${e}`));
    }
}

let currentLocation = window.location.href;

function handleLocationChange() {
    // _paq must be set at this point
    const _paq = window._paq;
    _paq.push(['setReferrerUrl', currentLocation]);
    currentLocation = window.location.href;
    _paq.push(['setCustomUrl', currentLocation]);
    _paq.push(['deleteCustomVariables', 'page']);
    _paq.push(['trackPageView']);
    _paq.push(['enableLinkTracking']);
}

function* handleInitialize() {
    ensureLanguageKeyIsInUrl();
    const api: SettingsApiService = yield getContext('settingsApiService');
    const headers: { 'x-tnt': string } = yield call([api, api.getContextHeaders]);
    const tenant = getTenantByIdFromHeader(headers);
    if (tenant === Tenant.NO_TENANT) {
        yield put(logError('Cant bootstrap tenant specific resources, x-tnt header is missing or unknown. APP COULD NOT BE STARTED!'));
    } else {
        loadTenantResources(tenant);
        yield put(changeTenant(tenant));
        yield initializeServices();
    }
    yield put(applicationInitialized());
}

function* addTranslations() {
    i18n.addResources('de', de);
    i18n.addResources('en', en);
    i18n.addResources('fr', fr);
    i18n.addResources('it', it);

    yield;
}

function* initializeServices() {
    yield put(fetchCustomizationSettings());
    yield put(fetchLanguages());
    yield put(fetchAppointmentFinderSettings());
    yield put(fetchSecureInboxSettings());
    yield put(fetchSecureDocumentStoreSettings());
    yield put(fetchUserInfo());
    yield take([fetchUserInfoSuccess, fetchUserInfoFailure]);

    // @ts-expect-error: TODO: the generator return type cannot be inferred even though the generator has complete inferred return type annotations - disabling the error for now
    if (yield isUserAnonymous()) {
        const didSessionTimeout = /.*session-timeout=true.*/.test(window.location.search);
        const didUserLogout = /.*logout=true.*/.test(window.location.search);
        if (didSessionTimeout) {
            yield put(
                addNotification({
                    title: i18n.t('application.automatic-logout-note.title'),
                    text: i18n.t('application.automatic-logout-note.body'),
                    type: NotificationType.INFO,
                }),
            );
        }
        if (didUserLogout) {
            yield put(
                addNotification({
                    title: i18n.t('application.logout-note.title'),
                    text: i18n.t('application.logout-note.body'),
                    type: NotificationType.SUCCESS,
                }),
            );
        }

        return;
    }

    yield loginUserAccordingToRoles();
    yield initializeServicesForSignedInUsers();
    yield spawn(detectUserLogout);
}

function* loginUserAccordingToRoles() {
    const userInfo: IUserInfo = yield select(getUserInfo);
    if (userInfo.roles.includes(UserRole.STRONG)) {
        yield put(strongLogin());
    } else {
        yield put(login());
    }
}

function* handleUserHasNoRoles() {
    const customerBases: ICustomerBase[] = yield select(getCustomerBases);
    const userInfo: IUserInfo = yield select(getUserInfo);
    const userIsProspect: boolean = yield select(isUserProspect);

    if (!userIsProspect && (!customerBases.length || customerBases.every((cB) => !cB.permissions.length))) {
        yield put(
            addNotification({
                text: i18n.t('application.technical-error-note.body'),
                title: i18n.t('application.technical-error-note.title'),
                type: NotificationType.ERROR,
                unique: true,
            }),
        );
    } else if (!userInfo.roles.length) {
        yield put(
            addNotification({
                text: i18n.t('application.no-roles-warning.body'),
                title: i18n.t('application.no-roles-warning.title'),
                type: NotificationType.WARNING,
            }),
        );
    }
}

function* fetchVaultsAndUnreadDocumentCount(fetchVaultsRequest: IFetchVaultsRequest) {
    yield put(fetchVaults(fetchVaultsRequest));
    yield take([fetchVaultsSuccess, fetchVaultsFailure]);
    const vaults: IVault[] = yield select(getOrderedVaults);
    if (vaults?.length) {
        yield put(selectVault(vaults[0]));
    }
    yield put(fetchUnreadDocumentCount());
}

function* fetchPortalAccountsAndCustomerBases() {
    yield put(fetchCustomerBases());
    yield take([fetchCustomerBasesSuccess, fetchCustomerBasesFailure]);

    yield handleUserHasNoRoles();

    yield put(fetchUserPortalAccount());
    yield take([fetchUserPortalAccountSuccess, fetchUserPortalAccountFailure]);
    yield put(readDefaultCustomerBase());
    yield take([readDefaultCustomerBaseSuccess, readDefaultCustomerBaseFailure]);
    const defaultCustomerBase: ICustomerBase = yield select(getDefaultCustomerBase);
    yield put(selectCustomerBase(defaultCustomerBase));
    yield put(fetchAllPopupResponses());
}

function* fetchCustomerMasterData() {
    const api: SynchronizationApiService = yield getContext('synchronizationApiService');
    try {
        yield call([api, api.synchronizeCustomerData]);
    } finally {
        yield put(fetchUserPerson());
    }
}

function* fetchMailboxesAndSelectFirstMailbox(externalIdsParameter?: string[]) {
    yield put(fetchMailboxes(externalIdsParameter));
    yield take([fetchMailboxesSuccess, fetchMailboxesFailure]);
    const mailboxes: IUserMailbox[] = yield select(getOrderedMailboxes);
    yield put(selectMailboxes(mailboxes));
}

function* handleSelectCustomerBase({payload: selectedCustomerBase}: Action<ICustomerBase>) {
    const externalIds: string[] = [];
    const userInfo: IUserInfo = yield select(getUserInfo);
    if (!selectedCustomerBase) {
        externalIds.push(userInfo.loginId)
        const customerBases: ICustomerBase[] = yield select(getCustomerBases);
        externalIds.push(...customerBases.filter((customerBase) => customerBase.type === 'PRIVATE' && !customerBase.isSource)
            .map((customerBase) => customerBase.externalId));
        const sourceCustomerBase = customerBases.find((customerBase) => !!customerBase.isSource);
        yield put(resolveRmForCustomerBase(sourceCustomerBase));
        yield put(selectCustomerBaseForProducts(sourceCustomerBase));
        yield put(selectCustomerBaseForEServices(sourceCustomerBase));
        yield put(selectCustomerBaseForAppointment(sourceCustomerBase));
    } else {
        externalIds.push(selectedCustomerBase.externalId);
        yield put(resolveRmForCustomerBase(selectedCustomerBase));
        yield put(selectCustomerBaseForProducts(selectedCustomerBase));
        yield put(selectCustomerBaseForEServices(selectedCustomerBase));
        yield put(selectCustomerBaseForAppointment(selectedCustomerBase));
    }
    const externalIdsParameter = externalIds.length > 0 ? externalIds : undefined;

    yield spawn(fetchVaultsAndUnreadDocumentCount, {
        'external-id': externalIdsParameter,
    });
    yield spawn(fetchMailboxesAndSelectFirstMailbox, externalIdsParameter);

    const {resolveRmSuccess} = yield race({
        resolveRmSuccess: take(resolveRmForCustomerBaseSuccess),
        resolveRmFailure: take(resolveRmForCustomerBaseFailure),
    });

    yield put(setRelationshipManager(resolveRmSuccess && resolveRmSuccess.payload));
}

function* initializeServicesForSignedInUsers() {
    yield put(fetchTags());
    yield put(fetchMediaTypes());
    yield put(fetchUserPortalAccount());
    yield put(fetchTopics());
    yield spawn(fetchCustomerMasterData);
    yield spawn(fetchPortalAccountsAndCustomerBases);
}

function* handleAddNotification(notificationAction: Action<INotification>) {
    // Use of delay to put message to the end of the queue to allow page transitions before displaying the notification
    yield delay(0);
    yield put(addNotificationSuccess(notificationAction.payload));
}

function* handleAddNotificationClientAdvisory(notificationAction?: Action<INotificationClientAdvisory>) {
    const titleKey = notificationAction?.payload?.titleKey ?? 'notification.failure.client-advisory.title';
    const bodyKey = notificationAction?.payload?.bodyKey ?? 'notification.failure.client-advisory.body';

    // Use of delay to put message to the end of the queue to allow page transitions before displaying the notification
    yield delay(0);
    yield put(
        addNotificationSuccess({
            title: i18n.t(titleKey),
            textHtmlKey: i18n.t(bodyKey),
            textHtmlComponents: {
                linkContactForm: <Link to={t(RouteTranslationKey.CONTACT_FORM)}/>,
                linkPhone: <Link to={t(RouteTranslationKey.PHONE_OVERVIEW)}/>,
            },
            type: NotificationType.ERROR,
        }),
    );
}

function* handleChangeLocation(action: Action<IApplicationLocation<any, any>>) {
    const {location, params, state} = action.payload;
    try {
        yield put(push(generatePath(location, params), state));
        yield put(changeLocationSuccess());
    } catch (error: unknown) {
        yield put(logError(`could not change location: ${error}`));
        yield put(changeLocationFailure());
    }
}

function* handleChangeLocationSuccess() {
    yield scrollWindowToTop();
}

function* detectUserLogout(): any {
    yield delay(loginStatusCheckIntervalInMs);
    yield put(fetchUserInfo());
    yield take([fetchUserInfoSuccess, fetchUserInfoFailure]);
    if (yield isUserAnonymous()) {
        window.location.replace(AuthenticationUrl.SESSION_TIMEOUT);
    } else {
        yield detectUserLogout();
    }
}

function handleLogout() {
    window.location.replace(AuthenticationUrl.LOGOUT);
}

function* isUserAnonymous() {
    const userInfo: IUserInfo = yield select(getUserInfo);

    return !userInfo || userInfo.anonymous;
}

function* handleRemoveCalculationSuccess() {
    yield put(
        addNotification({
            text: i18n.t('stored-calculation-box.remove-calculations.success.body'),
            title: i18n.t('stored-calculation-box.remove-calculations.success.title'),
            type: NotificationType.SUCCESS,
        }),
    );
    yield put(onlineCalculatorsApiActions.getStoredCalculations());
}

function* handleRemoveCalculationFailure() {
    yield put(
        addNotification({
            text: i18n.t('stored-calculation-box.remove-calculations.failure.body'),
            title: i18n.t('stored-calculation-box.remove-calculations.failure.title'),
            type: NotificationType.ERROR,
        }),
    );
    yield put(onlineCalculatorsApiActions.getStoredCalculations());
}

function* handleStoreCalculationSuccess() {
    const isToCloneCalculation: boolean = yield select(onlineCalculatorsApiSelectors.isToCloneCalculation);
    const isDownloadingCalculationPdf: boolean = yield select(onlineCalculatorsApiSelectors.isDownloadingCalculationPdf);

    if (!isToCloneCalculation && !isDownloadingCalculationPdf) {
        yield put(
            addNotification({
                title: i18n.t('stored-calculation-box.store-calculation.success.title'),
                type: NotificationType.SUCCESS,
            }),
        );
        yield put(onlineCalculatorsApiActions.getStoredCalculations());
    }
}

function* handleStoreCalculationFailure() {
    yield put(
        addNotification({
            text: i18n.t('stored-calculation-box.store-calculation.failure.body'),
            title: i18n.t('stored-calculation-box.store-calculation.failure.title'),
            type: NotificationType.ERROR,
        }),
    );
    yield put(onlineCalculatorsApiActions.getStoredCalculations());
}

function* handleCloneCalculationSuccess() {
    yield put(
        addNotification({
            text: i18n.t('online-calculators.calculators.clone-calculation.success.body'),
            title: i18n.t('online-calculators.calculators.clone-calculation.success.title'),
            type: NotificationType.SUCCESS,
        }),
    );
}

function* handleCloneCalculationFailure() {
    yield put(hideProgressIndicator());
    yield put(
        addNotification({
            text: i18n.t('online-calculators.calculators.clone-calculation.failure.body'),
            title: i18n.t('online-calculators.calculators.clone-calculation.failure.title'),
            type: NotificationType.ERROR,
        }),
    );
}

export function* applicationSaga() {
    yield take(initializeApplication);
    yield all([
        call(addTranslations),
        call(handleInitialize),
        takeEvery(LOCATION_CHANGE, handleLocationChange),
        takeEvery(addNotification, handleAddNotification),
        takeEvery(addNotificationClientAdvisory, handleAddNotificationClientAdvisory),
        takeEvery(changeLocation, handleChangeLocation),
        takeEvery(changeLocationSuccess, handleChangeLocationSuccess),
        takeEvery(selectCustomerBase, handleSelectCustomerBase),
        takeEvery(onlineCalculatorsApiActions.OnlineCalculatorsApiActions.STORE_CALCULATION_SUCCESS, handleStoreCalculationSuccess),
        takeEvery(onlineCalculatorsApiActions.OnlineCalculatorsApiActions.STORE_CALCULATION_FAILURE, handleStoreCalculationFailure),
        takeEvery(onlineCalculatorsApiActions.OnlineCalculatorsApiActions.CLONE_CALCULATION_SUCCESS, handleCloneCalculationSuccess),
        takeEvery(onlineCalculatorsApiActions.OnlineCalculatorsApiActions.CLONE_CALCULATION_FAILURE, handleCloneCalculationFailure),
        takeEvery(onlineCalculatorsApiActions.OnlineCalculatorsApiActions.REMOVE_STORED_CALCULATION_SUCCESS, handleRemoveCalculationSuccess),
        takeEvery(onlineCalculatorsApiActions.OnlineCalculatorsApiActions.REMOVE_STORED_CALCULATION_FAILURE, handleRemoveCalculationFailure),
        takeEvery(logout, handleLogout),
        takeEvery(setConsentMatomo, handleSetConsentMatomo),
    ]);
}
